import { useState, type ReactNode } from "react";
import type { PosterStateResponse } from "@studio/shared";
import { navigate } from "../../lib/navigation";
import { pathForStep } from "../../lib/session";
import { PosterPreview } from "../../poster/PosterPreview";

/**
 * 「하던 포스터가 있어요」(목업 s4) — 다음 시간에 같은 코드·이름으로 들어오면 먼저 뜬다.
 * 🔑 같은 탭에서 한 번 넘기면 다시 안 뜬다(sessionStorage). 다른 컴퓨터에서는 다시 뜬다 — 그게 이 화면의 쓸모다.
 */
const seenKey = (workId: string) => `poster-resume-seen:${workId}`;

function seen(workId: string): boolean {
  try {
    return window.sessionStorage.getItem(seenKey(workId)) === "1";
  } catch {
    return true;
  }
}

function markSeen(workId: string): void {
  try {
    window.sessionStorage.setItem(seenKey(workId), "1");
  } catch {
    // 막힌 저장소(사생활 보호 창)면 매번 보일 뿐이다 — 작업은 잃지 않는다.
  }
}

export function hasPosterProgress(state: PosterStateResponse): boolean {
  return Boolean(state.doc) || state.cuts.some((cut) => cut.image_url || cut.active_job);
}

function lastEdited(iso: string): string {
  const date = new Date(iso);
  return new Intl.DateTimeFormat("ko-KR", { timeZone: "Asia/Seoul", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" })
    .format(date);
}

export function PosterResumeGate({ state, children }: { state: PosterStateResponse; children: ReactNode }) {
  const [open, setOpen] = useState(() => {
    const show = !seen(state.work_id) && hasPosterProgress(state);
    // 🔑 이 탭에서 이미 작업 중이었다(처음 들어올 땐 아직 아무것도 없었다) — 단계를 옮길 때 다시 띄우지 않는다.
    if (!show) markSeen(state.work_id);
    return show;
  });
  if (!open) return <>{children}</>;
  return (
    <main className="poster-resume">
      <section className="poster-resume-card">
        <div className="poster-resume-thumb">
          <PosterPreview state={state} />
        </div>
        <h1 tabIndex={-1}>하던 포스터가 있어요</h1>
        <p>{lastEdited(state.updated_at)}에 마지막으로 고쳤어요.<br />다른 컴퓨터에서 들어와도 그대로 이어져요.</p>
        <button
          className="button-primary full-width"
          type="button"
          onClick={() => {
            markSeen(state.work_id);
            setOpen(false);
            navigate(pathForStep(state.work_id, state.step), { replace: true });
          }}
        >이어서 하기</button>
        <p className="poster-resume-note">내 이름이 아니라면 선생님께 말해 주세요</p>
      </section>
    </main>
  );
}
